import { supabase } from '../supabaseClient';

// Notification types
export interface Notification {
  id: string;
  user_id: string;
  type: 'new_project' | 'project_approved' | 'offer_received' | 'offer_accepted' | 'review_received' | 'credit_purchase' | 'account_approved';
  title: string;
  message: string;
  data?: any; // Additional data like project_id, offer_id, etc.
  read_at: string | null;
  created_at: string;
}

export interface NotificationFilters {
  type?: string;
  read?: boolean;
  limit?: number;
  offset?: number;
}

// Get user notifications
export const getNotifications = async (
  filters: NotificationFilters = {}
): Promise<{ notifications: Notification[]; count: number; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { notifications: [], count: 0, error: 'Не сте влезли в системата' };
    }

    const {
      type,
      read,
      limit = 20,
      offset = 0
    } = filters;

    let query = supabase
      .from('notifications')
      .select('*', { count: 'exact' })
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (type) {
      query = query.eq('type', type);
    }

    if (read !== undefined) {
      if (read) {
        query = query.not('read_at', 'is', null);
      } else {
        query = query.is('read_at', null);
      }
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching notifications:', error);
      return { notifications: [], count: 0, error: 'Грешка при зареждане на известията' };
    }

    return {
      notifications: data || [],
      count: count || 0
    };
  } catch (error) {
    console.error('Error in getNotifications:', error);
    return { notifications: [], count: 0, error: 'Неочаквана грешка' };
  }
};

// Get unread notifications count
export const getUnreadCount = async (): Promise<{ count: number; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { count: 0, error: 'Не сте влезли в системата' };
    }

    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .is('read_at', null);

    if (error) {
      console.error('Error fetching unread count:', error);
      return { count: 0, error: 'Грешка при зареждане на броя на известията' };
    }

    return { count: count || 0 };
  } catch (error) {
    console.error('Error in getUnreadCount:', error);
    return { count: 0, error: 'Неочаквана грешка' };
  }
};

// Mark notification as read
export const markAsRead = async (notificationId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'Не сте влезли в системата' };
    }

    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', notificationId)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error marking notification as read:', error);
      return { success: false, error: 'Грешка при маркиране на известието' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in markAsRead:', error);
    return { success: false, error: 'Неочаквана грешка' };
  }
};

// Mark all notifications as read
export const markAllAsRead = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'Не сте влезли в системата' };
    }

    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .is('read_at', null);

    if (error) {
      console.error('Error marking all notifications as read:', error);
      return { success: false, error: 'Грешка при маркиране на всички известия' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in markAllAsRead:', error);
    return { success: false, error: 'Неочаквана грешка' };
  }
};

// Create notification
export const createNotification = async (
  userId: string,
  type: Notification['type'],
  title: string,
  message: string,
  data?: any
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('notifications')
      .insert({
        user_id: userId,
        type,
        title,
        message,
        data
      });

    if (error) {
      console.error('Error creating notification:', error);
      return { success: false, error: 'Грешка при създаване на известието' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in createNotification:', error);
    return { success: false, error: 'Неочаквана грешка' };
  }
};

// Create notification for new project (for all freelancers and agencies)
export const createNewProjectNotification = async (
  projectId: string,
  projectTitle: string,
  projectDescription: string,
  budgetMin?: number,
  budgetMax?: number,
  categoryName?: string
): Promise<{ success: boolean; sent: number; errors: string[] }> => {
  try {
    // Get all freelancers and agencies
    const { data: providers, error: providersError } = await supabase
      .from('profiles')
      .select('id')
      .in('role', ['FREELANCER', 'AGENCY'])
      .eq('status', 'ACTIVE');

    if (providersError) {
      console.error('Error fetching providers:', providersError);
      return { success: false, sent: 0, errors: [providersError.message] };
    }

    if (!providers || providers.length === 0) {
      return { success: true, sent: 0, errors: [] };
    }

    const title = 'Нов проект в Workio!';
    const message = `Има нов проект "${projectTitle}" който може да ви заинтересува.`;
    const data = {
      project_id: projectId,
      project_title: projectTitle,
      project_description: projectDescription,
      budget_min: budgetMin,
      budget_max: budgetMax,
      category_name: categoryName
    };

    // Create notifications for all providers
    const results = await Promise.allSettled(
      providers.map(provider =>
        createNotification(provider.id, 'new_project', title, message, data)
      )
    );

    const sent = results.filter(result => 
      result.status === 'fulfilled' && result.value.success
    ).length;

    const errors = results
      .filter(result => result.status === 'rejected' || 
        (result.status === 'fulfilled' && !result.value.success))
      .map(result => 
        result.status === 'rejected' 
          ? result.reason?.message || 'Unknown error'
          : result.value.error || 'Unknown error'
      );

    return { success: true, sent, errors };
  } catch (error) {
    console.error('Error in createNewProjectNotification:', error);
    return { 
      success: false, 
      sent: 0, 
      errors: [error instanceof Error ? error.message : 'Unknown error'] 
    };
  }
};

// Delete notification
export const deleteNotification = async (notificationId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, error: 'Не сте влезли в системата' };
    }

    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error deleting notification:', error);
      return { success: false, error: 'Грешка при изтриване на известието' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in deleteNotification:', error);
    return { success: false, error: 'Неочаквана грешка' };
  }
};
